import { XClose } from '@untitledui/icons'
import { type FC, isValidElement, type ReactNode } from 'react'
import { twMerge } from 'tailwind-merge'

import Button from '@components/designSystem/Button'
import { useIsWideLayout } from '@hooks/useMediaQuery.ts'
import { cx } from '@utils/cx'
import { isReactComponent } from '@utils/is-react-component.ts'

import { type BaseDialogProps } from './DialogContext'

type DialogSize = 'sm' | 'md' | 'lg' | 'xl' | 'full'

interface DialogProps extends BaseDialogProps {
  children?: ReactNode
  title?: string
  description?: string
  size?: DialogSize
  showCloseButton?: boolean
  footer?: ReactNode
  headerIcon?: FC<{ className?: string }> | ReactNode
  headerIconContainerClassName?: string
  testId?: string
}

const sizeClasses: Record<DialogSize, string> = {
  sm: 'w-96 max-w-sm',
  md: 'w-[32rem] max-w-md',
  lg: 'w-[48rem] max-w-2xl',
  xl: 'w-[64rem] max-w-4xl',
  full: 'w-full h-full max-w-none max-h-none',
}

/**
 * A versatile Dialog component that adapts to different screen sizes and use cases.
 * On desktop, it appears as a centered modal, while on mobile, it transforms into a bottom drawer.
 * The dialog can be configured to be fullscreen, and includes optional header, footer, and close button.
 *
 * @param children - The main content of the dialog
 * @param title - Optional title for the dialog header
 * @param description - Optional description for the dialog header
 * @param size - Size of the dialog: 'sm', 'md', 'lg', 'xl', or 'full' (default: 'md')
 * @param showCloseButton - Whether to show the close button in the header (default: true)
 * @param footer - Optional footer content, typically for action buttons
 * @param HeaderIcon
 * @param headerIconContainerClassName
 * @param onClose - Callback function when the dialog is requested to close
 * @param className - Additional CSS classes for custom styling
 * @param testId
 */
function Dialog({
  children,
  title,
  description,
  size = 'md',
  showCloseButton = true,
  footer,
  headerIcon: HeaderIcon,
  headerIconContainerClassName,
  onClose,
  className,
  testId,
}: DialogProps) {
  const isWideLayout = useIsWideLayout()
  const shouldShowHeader = Boolean(HeaderIcon) || showCloseButton

  // Determine behavior based on size and device
  const isFullscreen = size === 'full'
  const isDrawer = !isWideLayout && !isFullscreen

  const ResolvedIcon = isValidElement(HeaderIcon) ? HeaderIcon : isReactComponent(HeaderIcon) ? <HeaderIcon className={`
    size-5
  `} /> : null

  return (
    <div
      className={cx(
        'flex flex-col gap-4 overflow-visible bg-bg-primary',
        isDrawer ? 'px-6 pt-6 pb-4' : 'p-6',
        // Fullscreen styling (both mobile and desktop)
        isFullscreen && 'size-full max-h-none max-w-none rounded-none',
        // Drawer styling (mobile only, non-fullscreen)
        isDrawer && 'drawer-content',
        // Centered modal styling (desktop only, non-fullscreen)
        isWideLayout && !isFullscreen && 'fixed top-1/2 left-1/2 m-auto -translate-1/2 transform rounded-xl shadow-xl',
        isWideLayout && !isFullscreen && sizeClasses[size],
        className
      )}
      data-testid={testId}
    >
      {/* Header */}
      {shouldShowHeader && (
        <div className="flex items-center justify-between">
          <div className="flex-1">
            {ResolvedIcon ?
              <div className={twMerge(`
                flex size-10 items-center justify-center rounded-lg border border-secondary shadow-xs-skeumorphic
              `, headerIconContainerClassName)}>
                {ResolvedIcon}
              </div>
              : null
            }
          </div>
          {showCloseButton && (
            <Button
              size='sm'
              variant='ghost'
              onClick={onClose}
              aria-label="Close dialog"
            >
              <XClose />
            </Button>
          )}
        </div>
      )}

      {
        (title ?? description) && (
          <div className='flex flex-col gap-1'>
            <h1 className='text-md font-medium'>{title}</h1>
            <p className='text-sm text-secondary'>{description}</p>
          </div>
        )
      }

      {/* Content */}
      {children && (
        <div className={cx(isFullscreen && 'flex-1')}>
          {children}
        </div>
      )}

      {/* Footer */}
      {footer && (
        <div className="flex items-center justify-end gap-3">
          {footer}
        </div>
      )}
    </div>
  )
}

export default Dialog